'use client';

import React, { ReactNode, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { string } from 'zod';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface TeamBlockCustomV2Props {
  title?: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  id?: string;
  infoArr?: {
    title: ReactNode | SerializedEditorState;
    subtitle?: ReactNode | SerializedEditorState;
    txt?: ReactNode | SerializedEditorState;
    srcImage: string | { url: string; alt: string };
  }[];
}

const defaultInfoArr = [
  {
    title: 'Вище керівництво',
    subtitle:
      'Ми присвячуємо свою працю тим, хто прагне знань. Нехай у наших працівників не буде сумнівів, що тут дійсно народжується революція навчання, а в просторах нашого офісу створені найкращі умови для роботи, особистісного зростання та розвитку кар’єри. Всім прекрасним колективом ми кажемо вам, дорогі студенти, що: наше завдання — це ваша освіта, ваш успіх, ваш розвиток, бо AP Education створений для вас. Ми робимо все, щоб ви досягли всього. Щоб ви знайшли своє внутрішнє покликання і впевнено крокували вперед. Тож запрошуємо вас змінювати майбутнє разом з нами!',
    srcImage: '/assets/photoAP/Team/MykolaOwner.webp',
  },
  {
    title: 'Відділ HR',
    subtitle:
      'Відділ HR відіграє ключову роль у підтримці ефективності та гармонії персоналу. Основне завдання HR-відділу: забезпечення компанії персоналом та створення сприятливого середовища для розвитку співробітників. Відділ складається з HR-керівника та рекрутерів.',
    txt: '“Тільки з часом ми зрозуміли, як багато від нас залежить. Ми не лише зустрічаємо нових людей, ми формуємо культуру для того, щоб збудувати велике.”',
    srcImage: '/assets/photoAP/Team/hr.webp',
  },
  {
    title: 'Відділ освіти',
    subtitle:
      'Цей відділ — основа нашої компанії, адже саме від того, як ми будемо навчати, залежить наш успіх.',
    txt: '“Ми створюємо унікальні програми відповідно до передових міжнародних стандартів викладання. Проводимо регулярне підвищення кваліфікації викладачів для того, аби забезпечити найкраще навчання. Наші викладачі працюють індивідуально з кожним студентом. І головне: ми працюємо лише в інтерактивному ігровому форматі, жодних підручників.”',
    srcImage: '/assets/photoAP/Team/osvita.webp',
  },
  {
    title: 'Відділ сервісу',
    subtitle:
      'Якщо відділ освіти відповідальний за ефективність навчання, то відділ сервісу — за якість надання навчальних послуг. Його мета полягає у забезпеченні інформацією, наданні технічної та психологічної підтримки, мотивації, а також побудова довгострокових відносин з клієнтами.',
    txt: '“Сервіс — це друг, допомога, підтримка і мотивація студента. Адже навчання, все таки, важкий шлях, на якому трапляється багато перепон. Навіть з нашим комплексним форматом, який його полегшує.”',
    srcImage: '/assets/photoAP/Team/servis.webp',
  },
  {
    title: 'Відділ продаж',
    subtitle:
      'Відділ продажу є частиною компанії, що відповідає за генерацію доходів через продаж послуг компанії. Мета відділу: забезпечення максимального обсягу продажів послуг компанії, а також якісна консультація та збереження залученості клієнтів.',
    txt: '“Кожна наша взаємодія з клієнтом — це можливість створити щось по-справжньому цінне, навіть більше, ніж він очікує.”',
    srcImage: '/assets/photoAP/Team/sales.webp',
  },
  {
    title: 'Відділ маркетингу',
    subtitle:
      'Відділ маркетингу відповідає за розробку та реалізацію стратегій для просування компанії, дослідження ринку, медіавміст та рекламу, взаємодію з клієнтами, а також аналіз ефективності стратегії та її складників.',
    txt: '“AP Education несе людям прості ідеї. Але донести їх дуже непросто, чесно кажучи, бо навчатися по-новому, змінити старі звички на революційні за короткий час можуть не всі.”',
    srcImage: '/assets/photoAP/Team/marketing.webp',
  },
  {
    title: 'Відділ адміністрування',
    subtitle:
      'Працівники відділу відповідальні за ефективне функціонування офісу, створення сприятливих умов роботи для працівників, координацію співпраці всередині компанії. А також за вирішення технічних питань.',
    txt: '“Часто чути, що робота є другим домом. Ми не будемо це повторювати, бо справжній дім для кожної людини ні з чим не зрівняється. Але тут ви теж знайдете підтримку й комфорт, тут вас теж приймуть.”',
    srcImage: '/assets/photoAP/Team/admin.webp',
  },
  {
    title: 'Відділ автоматизації бізнес-процесів',
    subtitle:
      'Цей відділ відповідає за розробку, налаштування та оптимізацію всіх бізнес-процесів та процесів автоматизації у компанії. Основні завдання: підтримка безперебійної роботи апаратного забезпечення, оптимізація технічних процесів, налаштування та адміністрування комп’ютерного устаткування.',
    txt: '“Не буває такого, щоб все працювало ідеально. Та наша команда йде до ідеалу.”',
    srcImage: '/assets/photoAP/Team/operation.webp',
  },
];

export default function TeamBlockCustomV2({
  title = 'Default Title',
  subtitle = 'Default Subtitle',
  id,
  infoArr = defaultInfoArr,
}: TeamBlockCustomV2Props) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? infoArr.length - 1 : prevIndex - 1));
  };

  const handleNextClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === infoArr.length - 1 ? 0 : prevIndex + 1));
  };

  const currentInfo = infoArr[currentIndex];

  const backgroundColorClass = 'custom-gradient-green-btn-h';
  const buttonClasses = `p-2 max-h-12 border border-gray-400 text-gray-400 rounded-full hover:border-white hover:text-white ${backgroundColorClass}`;

  return (
    <div data-meta="TeamBlockCustomV2" className="container mx-auto px-4 sm:px-6 lg:px-8 my-24">
      <div className="mx-auto text-center">
        <div className="text-white">
          <CustomRichText
            data={subtitle}
            tag="h2"
            className="text-4xl font-bold tracking-tight text-white sm:text-6xl mb-4 sm:mb-8"
          />
        </div>
        {subtitle && (
          <div className="text-white">
            <CustomRichText
              data={subtitle}
              tag="div"
              className="mt-6 text-sm md:text-md lg:text-xl md:leading-6 lg:leading-8 text-white"
            />
          </div>
        )}
      </div>
      <div className="px-4 py-5 sm:p-6 mb-8 flex flex-col max-lg:justify-center gap-8 lg:grid grid-cols-12">
        {/* Info Section */}
        <div className="bg-white rounded-3xl flex flex-col justify-between p-4 xl:p-8 col-span-8 w-full h-full min-h-[475px]">
          <div>
            <div className="flex justify-between m-2 pb-4 border-b-2 border-green-100">
              <CustomRichText data={currentInfo.title} tag="div" className="text-xl font-bold" />
              <div className="flex gap-x-2">
                <button onClick={handlePrevClick} className={buttonClasses}>
                  <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <button onClick={handleNextClick} className={buttonClasses}>
                  <ArrowRightIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            {currentInfo.subtitle && (
              <div className="p-2">
                <CustomRichText data={currentInfo.subtitle} tag="div" />
              </div>
            )}
            {currentInfo.txt && (
              <div className="p-2">
                <CustomRichText data={currentInfo.txt} tag="div" />
              </div>
            )}
          </div>
        </div>

        {/* Image Section */}
        <div className="col-span-4">
          <Image
            alt={''}
            src={
              typeof currentInfo.srcImage === 'string'
                ? currentInfo.srcImage
                : currentInfo.srcImage?.url
            }
            width={750}
            height={500}
            style={{ width: '100%', height: 'auto' }}
            className="w-full h-full rounded-2xl object-contain aspect-auto"
          />
        </div>
      </div>
    </div>
  );
}
